'use client';

import { Drawer as DrawerPrimitive, type DialogProps as VaultDrawerProps } from 'vaul';
import { cn, Flex, IconButton } from '@bridebook/aura';
import { Close } from '@bridebook/aura/src/icons';
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  useMemo,
  type ReactNode,
} from 'react';
import { ModalControlContext, ModalControlContextType } from '../modal/modal-control-context';
import { DrawerContext, useDrawerContext } from './drawer-context';

export type DrawerProps = {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  children?: ReactNode;
  forceClose?: ModalControlContextType['forceClose'];
  classNames?: {
    overlay?: string;
  };
  /**
   * An array of indexes from the `snapPoints` array where internal content scrolling is disabled.
   * * This is useful for ensuring that content only becomes scrollable once the drawer reaches
   * a specific height (usually the fully expanded state). For example, if
   * `snapPoints={['300px', 1]}` and `disableScrollInSnapPointIndexes={[0]}`, the content
   * will be locked at the 300px mark and only become scrollable when swiped up to 100%.
   */
  disableScrollInSnapPointIndexes?: number[];
} & Omit<
  VaultDrawerProps,
  // due to a known issue when modal = false
  // we're deactivating this prop for now
  // and only using the drawer as modal
  // https://github.com/emilkowalski/vaul/issues/497
  'open' | 'modal'
>;

export const Drawer = forwardRef<ElementRef<typeof DrawerPrimitive.Content>, DrawerProps>(
  (
    {
      children,
      isOpen,
      onOpenChange,
      forceClose,
      className,
      classNames,
      disableScrollInSnapPointIndexes,
      activeSnapPoint,
      snapPoints = [],
      ...props
    },
    ref,
  ) => {
    const controlContextValue = useMemo(
      () =>
        ({
          isOpen: isOpen ?? false,
          onOpenChange,
          close: () => onOpenChange?.(false),
          forceClose,
        }) satisfies ModalControlContextType,
      [isOpen, onOpenChange, forceClose],
    );

    const hasSnapPoints = snapPoints.length > 0;

    const shouldDisableScrollInSnapPoint = useMemo(() => {
      if (!hasSnapPoints || !activeSnapPoint || !disableScrollInSnapPointIndexes) {
        return false;
      }

      // Get the snap point values from the indexes
      const disabledValues = disableScrollInSnapPointIndexes.map((index) => snapPoints[index]);

      // Check if the current activeSnapPoint
      // is in the disabled list
      return disabledValues.some((val) => String(val) === String(activeSnapPoint));
    }, [snapPoints, hasSnapPoints, activeSnapPoint, disableScrollInSnapPointIndexes]);

    const drawerContextValue = useMemo(
      () => ({
        shouldDisableScrollInSnapPoint,
        hasSnapPoints,
      }),
      [shouldDisableScrollInSnapPoint, hasSnapPoints],
    );

    return (
      <ModalControlContext.Provider value={controlContextValue}>
        <DrawerContext.Provider value={drawerContextValue}>
          <DrawerPrimitive.Root
            data-slot="drawer"
            open={isOpen}
            onOpenChange={onOpenChange}
            // Vaul by default has repositionInputs set to true.
            // It causes strange issues when opening mobile keyboard.
            // Works better without it.
            repositionInputs={false}
            direction="bottom" // fixed to bottom. If ever needed, we can add more directions.
            // Only pass snapPoints props if they exist
            // otherwise dragging behavior is deactivated
            {...(hasSnapPoints && {
              activeSnapPoint,
              snapPoints,
            })}
            modal
            {...(props as VaultDrawerProps)}>
            <DrawerPortal>
              <DrawerOverlay className={classNames?.overlay} />
              <DrawerPrimitive.Content
                aria-describedby={undefined}
                ref={ref}
                data-slot="drawer-content"
                className={cn(
                  'group/drawer-content fixed z-[10000] flex h-auto flex-col bg-white',
                  // direction="bottom" styling
                  'inset-x-0 bottom-0 mt-24 max-h-[80vh] rounded-t-2xl',
                  'pb-safe-area-inset-bottom',
                  hasSnapPoints && 'h-full max-h-[calc(99%-env(safe-area-inset-top))]',
                  className,
                )}>
                {children}
              </DrawerPrimitive.Content>
            </DrawerPortal>
          </DrawerPrimitive.Root>
        </DrawerContext.Provider>
      </ModalControlContext.Provider>
    );
  },
);
Drawer.displayName = 'Drawer';

const DrawerPortal = ({ ...props }: ComponentPropsWithoutRef<typeof DrawerPrimitive.Portal>) => (
  <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />
);

export const DrawerClose = ({
  ...props
}: ComponentPropsWithoutRef<typeof DrawerPrimitive.Close>) => (
  <DrawerPrimitive.Close data-slot="drawer-close" {...props} asChild>
    <IconButton variant="grey" size="sm" icon={Close} />
  </DrawerPrimitive.Close>
);

const DrawerOverlay = forwardRef<
  ElementRef<typeof DrawerPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    data-slot="drawer-overlay"
    className={cn(
      'fixed inset-0 z-[10000] bg-navy-growth/70 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
DrawerOverlay.displayName = 'DrawerOverlay';

const DrawerTopBarHandle = ({ className }: ComponentPropsWithoutRef<'div'>) => (
  <div
    className={cn(
      'absolute top-3 left-1/2 hidden h-1 w-[60px] shrink-0 -translate-x-1/2 rounded-full bg-space-15 group-data-[vaul-drawer-direction=bottom]/drawer-content:block',
      className,
    )}
  />
);

export type DrawerTopBarProps = ComponentPropsWithoutRef<'div'> & {
  /** When false, the close button is hidden and children take the full width. */
  withCloseButton?: boolean;
  /**
   * Controls the content layout when `withCloseButton` is true.
   * - `'left'`: children are left-aligned and fill the space up to the close button.
   * - `'center'`: children are centered between equal-width columns mirroring the close button side,
   *   so a title stays centered and is constrained to avoid being hidden by the button.
   */
  layout?: 'left' | 'center';
  classNames?: {
    wrapper?: string;
  };
};
export const DrawerTopBar = ({
  children,
  className,
  withCloseButton = true,
  layout = 'center',
  classNames,
  ...props
}: DrawerTopBarProps) => {
  const { hasSnapPoints } = useDrawerContext();
  return (
    <div
      data-slot="drawer-top-bar"
      className={cn(
        'flex px-4 py-4',
        withCloseButton && layout === 'center' && 'grid grid-cols-[1fr_auto_1fr]',
        withCloseButton && layout === 'left' && 'justify-between',
        // If there are children add more top padding for the handle
        children && 'pt-5',
        classNames?.wrapper,
      )}
      {...props}>
      {hasSnapPoints && <DrawerTopBarHandle />}
      {withCloseButton && layout === 'center' && <div className="h-8 w-8" />}
      <Flex className={cn('flex-1', layout === 'center' && 'items-center text-center', className)}>
        {children}
      </Flex>
      {withCloseButton && (
        <Flex className="justify-end">
          <DrawerClose />
        </Flex>
      )}
    </div>
  );
};

export const DrawerBody = ({ className, ...props }: ComponentPropsWithoutRef<'div'>) => {
  const { shouldDisableScrollInSnapPoint } = useDrawerContext();
  return (
    <div
      data-slot="drawer-body"
      className={cn(
        'mx-auto scrollbar-hidden w-full max-w-[460px] overflow-y-auto',
        // If this is the first element in the container (no top bar),
        // increase the top spacing
        'first:mt-8',
        shouldDisableScrollInSnapPoint && 'flex flex-1 flex-col',
        className,
      )}
      {...props}
    />
  );
};
DrawerBody.displayName = 'DrawerBody';

export const DrawerHeader = ({ className, ...props }: ComponentPropsWithoutRef<'div'>) => (
  <div
    data-slot="drawer-header"
    className={cn(
      'px-5 pb-2',
      // If this is the last element in the container (no content and no footer),
      // increase the bottom padding
      'last:pb-5',
      className,
    )}
    {...props}
  />
);

export const DrawerContent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(
  ({ className, ...props }, ref) => {
    const { shouldDisableScrollInSnapPoint } = useDrawerContext();
    return (
      <div
        ref={ref}
        data-slot="drawer-content"
        className={cn(
          'scrollbar-hidden overflow-auto px-5 py-2',
          // If this is the last element in the body (no footer),
          // increase the bottom padding
          'last:pb-5',
          shouldDisableScrollInSnapPoint && 'overflow-hidden',
          className,
        )}
        {...props}
      />
    );
  },
);
DrawerContent.displayName = 'DrawerContent';

export const DrawerFooter = ({ className, ...props }: ComponentPropsWithoutRef<'div'>) => (
  <div
    data-slot="drawer-footer"
    className={cn('mt-auto flex flex-col gap-4 px-5 pt-2 pb-5', className)}
    {...props}
  />
);

type DrawerTitleProps = ComponentPropsWithoutRef<typeof DrawerPrimitive.Title> & {
  size?: 'standard' | 'lg';
};

export const DrawerTitle = forwardRef<ElementRef<typeof DrawerPrimitive.Title>, DrawerTitleProps>(
  ({ className, size = 'standard', ...props }, ref) => (
    <DrawerPrimitive.Title
      ref={ref}
      data-slot="drawer-title"
      className={cn(
        'm-0 text-space',
        'text-center',
        size === 'standard' && 'typo-base-semibold',
        size === 'lg' && 'typo-lg-semibold',
        className,
      )}
      {...props}
    />
  ),
);
DrawerTitle.displayName = 'DrawerTitle';

export const DrawerDescription = forwardRef<
  ElementRef<typeof DrawerPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    data-slot="drawer-description"
    className={cn('m-0 typo-base text-space', className)}
    {...props}
  />
));
DrawerDescription.displayName = 'DrawerDescription';
